// WebSocket Service for Real-time Updates
import { n8nConfig } from '../config/n8n.config';

export interface WebSocketMessage {
  type: 'task-created' | 'task-updated' | 'task-deleted' | 'task-moved' | 'task-completed' | 'task-archived' | 'task-unarchived' | 'transcription-complete' | 'subscribe' | 'subscribed';
  data?: any;
  timestamp: string;
}

export type WebSocketEventHandler = (message: WebSocketMessage) => void;

class WebSocketService {
  private ws: WebSocket | null = null;
  private eventHandlers: Map<string, WebSocketEventHandler[]> = new Map();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = process.env.NODE_ENV === 'production' ? 2 : 5;
  private reconnectDelay = 1000; // Start with 1 second
  private isManuallyDisconnected = false;
  private connectionPromise: Promise<void> | null = null;

  constructor() {
    // Only connect when explicitly enabled to avoid console errors
    if (process.env.REACT_APP_ENABLE_WEBSOCKET === 'true') {
      console.log('🔌 WebSocket enabled - connecting...');
      this.connect();
    } else {
      console.log('🔌 WebSocket disabled via REACT_APP_ENABLE_WEBSOCKET env var');
    }
  }

  private getWebSocketUrl(): string {
    const baseUrl = n8nConfig.baseUrl;
    // Convert HTTP(S) URL to WebSocket URL
    const wsUrl = baseUrl.replace(/^https?:\/\//, (match) => 
      match === 'https://' ? 'wss://' : 'ws://'
    );
    return `${wsUrl}/ws`;
  }

  public connect(): Promise<void> {
    // Don't connect if WebSocket is disabled
    if (process.env.REACT_APP_ENABLE_WEBSOCKET !== 'true') {
      console.log('🔌 WebSocket connection skipped - disabled via environment variable');
      return Promise.resolve();
    }

    if (this.connectionPromise) {
      return this.connectionPromise;
    }

    this.connectionPromise = new Promise((resolve, reject) => {
      try {
        this.isManuallyDisconnected = false;
        const wsUrl = this.getWebSocketUrl();
        console.log('🔌 Connecting to WebSocket:', wsUrl);
        
        this.ws = new WebSocket(wsUrl);

        this.ws.onopen = () => {
          console.log('WebSocket connected successfully');
          this.reconnectAttempts = 0;
          this.reconnectDelay = 1000;
          
          // Subscribe to updates
          this.send({
            type: 'subscribe',
            data: {},
            timestamp: new Date().toISOString()
          });
          
          resolve();
        };

        this.ws.onmessage = (event) => {
          try {
            const message: WebSocketMessage = JSON.parse(event.data);
            this.handleMessage(message);
          } catch (error) {
            console.error('Error parsing WebSocket message:', error);
          }
        };

        this.ws.onclose = (event) => {
          console.log('WebSocket connection closed:', event.code, event.reason);
          this.ws = null;
          this.connectionPromise = null;
          
          if (!this.isManuallyDisconnected && this.reconnectAttempts < this.maxReconnectAttempts) {
            this.scheduleReconnect();
          }
        };

        this.ws.onerror = (error) => {
          console.error('🔌 WebSocket error:', error);
          this.connectionPromise = null;
          // Don't spam console with errors if WebSocket is expected to fail (like on Vercel serverless)
          if (process.env.NODE_ENV !== 'production') {
            reject(error);
          } else {
            // In production, fail silently but still clean up
            resolve();
          }
        };

      } catch (error) {
        console.error('Failed to create WebSocket connection:', error);
        this.connectionPromise = null;
        reject(error);
      }
    });

    return this.connectionPromise;
  }

  private scheduleReconnect(): void {
    this.reconnectAttempts++;
    const delay = Math.min(this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1), 30000);
    
    console.log(`Scheduling WebSocket reconnect attempt ${this.reconnectAttempts} in ${delay}ms`);
    
    setTimeout(() => {
      if (!this.isManuallyDisconnected) {
        this.connect().catch(error => {
          console.error('Reconnection failed:', error);
        });
      }
    }, delay);
  }

  private handleMessage(message: WebSocketMessage): void {
    console.log('Received WebSocket message:', message);
    
    // Emit to specific event handlers
    const handlers = this.eventHandlers.get(message.type) || [];
    handlers.forEach(handler => {
      try {
        handler(message);
      } catch (error) {
        console.error('Error in WebSocket event handler:', error);
      }
    });

    // Emit to wildcard handlers
    const wildcardHandlers = this.eventHandlers.get('*') || [];
    wildcardHandlers.forEach(handler => {
      try {
        handler(message);
      } catch (error) {
        console.error('Error in WebSocket wildcard handler:', error);
      }
    });
  }

  public send(message: WebSocketMessage): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    } else {
      console.warn('WebSocket is not connected. Message not sent:', message);
    }
  }

  public addEventListener(eventType: string, handler: WebSocketEventHandler): void {
    if (!this.eventHandlers.has(eventType)) {
      this.eventHandlers.set(eventType, []);
    }
    this.eventHandlers.get(eventType)!.push(handler);
  }

  public removeEventListener(eventType: string, handler: WebSocketEventHandler): void {
    const handlers = this.eventHandlers.get(eventType);
    if (handlers) {
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    }
  }

  public disconnect(): void {
    this.isManuallyDisconnected = true;
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.connectionPromise = null;
  }

  public isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }

  public getConnectionState(): string {
    if (!this.ws) return 'DISCONNECTED';
    
    switch (this.ws.readyState) {
      case WebSocket.CONNECTING: return 'CONNECTING';
      case WebSocket.OPEN: return 'CONNECTED';
      case WebSocket.CLOSING: return 'CLOSING';
      case WebSocket.CLOSED: return 'DISCONNECTED';
      default: return 'UNKNOWN';
    }
  }
}

// Export singleton instance
export const websocketService = new WebSocketService();
export default websocketService;
